import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import AppHeader from '../components/AppHeader';

export default function DocTypeIndex() {
  const { org, collection } = useParams<{ org: string; collection: string }>();
  const [loading, setLoading] = useState(true);
  const [collectionName, setCollectionName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [requiresAuth, setRequiresAuth] = useState(false);
  const { session } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!org || !collection) return;

    async function loadDocs() {
      try {
        const fetch = session?.user
          ? api.docs.list(org!, collection!)
          : api.docs.listPublic(org!, collection!);

        const data = await fetch;

        if (data.single_doc && data.document) {
          navigate(`/${org}/${collection}/${data.document.slug}`, { replace: true });
          return;
        }

        const docList = data.documents ?? [];
        if (docList.length > 0) {
          const firstDoc = [...docList].sort((a, b) => a.display_order - b.display_order)[0];
          navigate(`/${org}/${collection}/${firstDoc.slug}`, { replace: true });
          return;
        }
      } catch (err: any) {
        if (err?.status === 401 || err?.status === 403) {
          setRequiresAuth(true);
        } else {
          setError(err?.message ?? 'Failed to load documentation');
        }
      } finally {
        setLoading(false);
      }
    }

    // Fetch collection name in parallel
    api.orgs.collection(org!, collection!)
      .then(c => setCollectionName(c.name))
      .catch(() => {});

    loadDocs();
  }, [org, collection, session, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading documentation...</p>
        </div>
      </div>
    );
  }

  if (requiresAuth) {
    const currentPath = window.location.pathname;
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Authentication Required</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            This documentation is private. Please sign in to access it.
          </p>
          <Link
            to={`/auth/login?redirect=${encodeURIComponent(currentPath)}`}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition"
          >
            Sign in to continue
          </Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col">
      <AppHeader />

      <main className="flex-1 max-w-5xl mx-auto px-4 lg:px-8 py-8 w-full">
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm p-8">
          <header className="mb-8">
            <h1 className="text-4xl font-bold mb-2 text-gray-900 dark:text-white">{collectionName || collection}</h1>
            <p className="text-gray-600 dark:text-gray-400">{org} / {collection}</p>
          </header>

          <div className="text-center py-12">
            <svg className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Documents Found</h3>
            <p className="text-gray-600 dark:text-gray-400">There are no documents in this collection yet.</p>
          </div>
        </div>
      </main>
    </div>
  );
}
